<template>
  <div class="flex flex-col gap-4">
    <div class="text-2xl text_color">Software Credits</div>

    <div v-if="loading" class="flex flex-col items-center justify-center gap-2 mt-20">
      <CustomLoader :size="28" />
    </div>
    <div v-else>
      <div class="flex flex-col gap-2 border_bottom pb-3">
        <div class="text-xl text_color">Credits Overview</div>
        <div class="flex flex-row justify-between">
          <div class="flex flex-col gap-2">
            <div class="text_color text-[15px]">Total Software Credits Remaining in the Current Billing Cycle</div>
            <div class="text_color text-2xl font-semibold">{{ softwareCredits }}</div>
          </div>
          <a-button class="primary_medium" @click="addCreditsModalVisible = true">Add Credits</a-button>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-2" :style="{ display: loading ? 'none' : '' }">
      <div class="text-xl text_color">Consumption</div>

      <CustomContainer padding="p-4">
        <div id="credits_summary"></div>
      </CustomContainer>
    </div>

    <div v-if="!loading && softwareActions.length > 0">
      <CustomTable :headers="headers" :data="softwareActions" :show-actions-column="false" :show-pagination="false" :add-side-border="true" :show-sort="false" height="calc(100vh - 270px)" />
    </div>

    <CustomModal v-model:visible="addCreditsModalVisible" title="Add Software Credits" width="32vw">
      <div class="secondary_color text-[13px]">Number of software credits</div>
      <div class="flex justify-center items-center gap-2">
        <a-input type="number" :min="0" class="input_medium mt-1 incentive-input" v-model:value="creditsAmount" @change="calculateCost" />
        <div class="text-sm link-prefix mt-1">credits</div>
      </div>
      <template #footer>
        <div class="flex flex-row justify-between w-full items-center">
          <div class="flex flex-col gap-0.5">
            <div class="secondary_color text-[13px]">Amount to pay</div>
            <div class="font-semibold text_color text-xl">${{ costCalculation.total || 0 }}</div>
            <div v-if="costCalculation.discount_amount" class="text-green-500 text-xs">You save ${{ costCalculation.discount_amount }} ({{ costCalculation.discount_percent }}% off)</div>
          </div>
          <a-button :loading="addingCredits" :disabled="addingCredits || !creditsAmount" class="primary_medium" @click="addCredits"> Pay Now </a-button>
        </div>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue';
import CustomTable from '@/components/custom/CustomTable.vue';
import ApexCharts from 'apexcharts';
import CustomContainer from '@/components/custom/CustomContainer.vue';
import CustomModal from '@/components/custom/CustomModal.vue';
import CustomLoader from '@/components/custom/CustomLoader.vue';
import DashboardService from '@/services/DashboardService';

const loading = ref(false);
const softwareCredits = ref(0);
const softwareActions = ref([]);

const headers = [
  { label: 'Action', key: 'title', width: 6 },
  { label: 'Software Credits Consumed', key: 'credits_consumed', width: 6 },
];

const creditsAmount = ref(0);
const addCreditsModalVisible = ref(false);
const addingCredits = ref(false);
const date_values = ref([]);
const data_series = ref([]);
const costCalculation = ref({});

const calculateCost = async () => {
  if (!creditsAmount.value) {
    costCalculation.value = {};
    return;
  }

  try {
    // Convert string to number
    const credits = parseInt(creditsAmount.value, 10);
    const response = await DashboardService.calculatePurchaseCredits(credits, 'software');
    if (response?.success) {
      costCalculation.value = response.data;
    }
  } catch (error) {
    console.error('Error calculating cost:', error);
  }
};

const addCredits = async () => {
  addingCredits.value = true;
  try {
    // Convert string to number
    const credits = parseInt(creditsAmount.value, 10);
    const response = await DashboardService.purchaseCustomCredits(credits, 'software');
    if (response?.success && response.url) {
      window.location.href = response.url; // Redirect to Stripe checkout
    }
  } catch (error) {
    console.error('Error purchasing credits:', error);
  } finally {
    addingCredits.value = false;
  }
};
const fetchSoftwareActions = async () => {
  loading.value = true;
  try {
    // Fetch credits overview
    const overviewResponse = await DashboardService.getCreditsOverview();
    if (overviewResponse?.success) {
      softwareCredits.value = overviewResponse.remaining_software_credits;
    }

    // Fetch current consumption
    const consumptionResponse = await DashboardService.getSoftwareConsumption();
    if (consumptionResponse?.success) {
      softwareActions.value = consumptionResponse.actions;
    }

    // Fetch consumption history
    const historyResponse = await DashboardService.getSoftwareConsumptionHistory();
    if (historyResponse?.success) {
      const history = historyResponse.history;

      // Process dates and series data
      date_values.value = history.map((item) => item.date.split('T')[0]);

      // Get unique action titles
      const actionTitles = [...new Set(history.flatMap((item) => Object.keys(item.breakdown)))];

      // Create series data
      data_series.value = actionTitles.map((title) => ({
        name: title,
        type: 'column',
        data: history.map((day) => day.breakdown[title] || 0),
      }));

      renderChart();
    }
  } catch (error) {
    console.error('Error fetching software actions:', error);
  } finally {
    loading.value = false;
  }
};

const renderChart = () => {
  // Wait for next tick to ensure DOM is ready
  nextTick(() => {
    const chartElement = document.querySelector('#credits_summary');
    if (!chartElement) return;

    // Cleanup existing chart if any
    chartElement.innerHTML = '';

    let options = buildChartData(date_values.value, data_series.value);
    let chart = new ApexCharts(chartElement, options);
    chart.render();
  });
};

const buildChartData = (date_values, data_series) => {
  const formatted_dates = date_values.map((dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); // Formats as "2 Mar"
  });
  let data_series_with_coordinates = data_series.map((series) => ({
    name: series.name,
    type: series.type,
    data: series.data ? series.data.map((value, index) => ({ x: formatted_dates[index], y: value })) : [],
  }));
  return {
    chart: {
      height: 380,
      width: '100%',
      type: 'bar',
      toolbar: { show: false },
      stacked: true,
      animations: {
        initialAnimation: {
          enabled: true,
        },
      },
      zoom: {
        enabled: false,
      },
    },
    grid: {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    colors: ['#A146E8', '#D89435', '#48AF7D', '#2579EF'],
    tooltip: {
      theme: 'dark',
    },
    fill: {
      type: 'gradient',
      gradient: {
        type: 'vertical',
        shadeIntensity: 1,
        opacityFrom: 0.14,
        opacityTo: 0,
        stops: [20, 100],
        colorStops: [
          [
            { offset: 0, color: 'rgba(161, 70, 232, 0.14)' },
            { offset: 100, color: 'rgba(161, 70, 232, 0)' },
          ],
          [
            { offset: 0, color: 'rgba(216, 148, 53, 0.14)' },
            { offset: 100, color: 'rgba(216, 148, 53, 0)' },
          ],
          [
            { offset: 0, color: 'rgba(72, 175, 125, 0.14)' },
            { offset: 100, color: 'rgba(72, 175, 125, 0)' },
          ],
          [
            { offset: 0, color: 'rgba(37, 121, 239, 0.14)' },
            { offset: 100, color: 'rgba(37, 121, 239, 0)' },
          ],
        ],
      },
    },
    title: {
      text: 'Software Credit Consumption',
      align: 'left',
      style: {
        fontSize: '16px',
        fontWeight: 'normal',
        color: '#fff',
      },
    },
    stroke: {
      show: true,
      width: 3,
      colors: ['#A146E8', '#D89435', '#48AF7D', '#2579EF'],
      lineCap: 'square',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '30%',
      },
    },
    series: data_series_with_coordinates,
    dataLabels: {
      enabled: false,
      style: {
        color: '#fff',
      },
    },
    labels: formatted_dates,
    yaxis: {
      tooltip: {
        theme: 'dark',
      },
      labels: {
        style: {
          colors: ['#fff'],
        },
      },
    },
    xaxis: {
      type: 'category',
      categories: formatted_dates,
      tickPlacement: 'on',
      title: {
        text: 'Date',
        style: {
          color: '#fff',
        },
      },
      axisBorder: {
        show: true, // Ensures baseline is shown
        color: 'rgba(255, 255, 255, 0.2)', // Matches grid line color
      },
      tooltip: {
        theme: 'dark',
      },
      labels: {
        datetimeUTC: false,
        style: {
          colors: Array(formatted_dates.length).fill('#fff'),
        },
      },
    },
  };
};

onMounted(async () => {
  await fetchSoftwareActions();
});
</script>
